import { useCallback, useEffect, useState } from "react"
import { Source } from "@/components/maps/mapLibre/Source"
import { Layer } from "@/components/maps/mapLibre/Layer"
import AgentMarker from "@/components/slickAI/AgentMarker"
import { polylineFeature } from "@/lib/aiWidgetMapHelpers"
import type { MapViewData } from "@/lib/aiWidgetMapParser"

type AgentMapLayersProps = {
  mapData: MapViewData
  // Used as part of React keys so children fully unmount when navigating
  // between maps in history.
  generation: number
}

const AgentMapLayers = ({ mapData, generation }: AgentMapLayersProps) => {
  // Track which marker (if any) has its popup open. Keeping the state up here
  // — instead of inside each AgentMarker — gives us "only one popup at a time"
  // for free: opening marker B re-renders A with isOpen=false.
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  // Reset on map switch (history nav, MAM replay, etc.) so a leftover popup
  // from the previous map doesn't ghost-render on the new one.
  useEffect(() => {
    setOpenIndex(null)
  }, [generation])

  const handleClose = useCallback(() => setOpenIndex(null), [])

  return (
    <>
      {mapData.polylines.map((pl, i) => (
        <Source
          key={`pl-${generation}-${i}`}
          type="geojson"
          data={polylineFeature(pl)}
        >
          <Layer
            type="line"
            layout={{ "line-join": "round", "line-cap": "round" }}
            paint={{
              "line-color": pl.color ?? "#3b82f6",
              "line-width": pl.width ?? 3,
            }}
          />
        </Source>
      ))}
      {mapData.markers.map((m, i) => (
        <AgentMarker
          key={`m-${generation}-${i}`}
          longitude={m.lon}
          latitude={m.lat}
          label={m.label}
          color={m.color}
          kind={m.kind}
          popup={m.popup}
          isOpen={openIndex === i}
          onToggle={() => setOpenIndex((current) => (current === i ? null : i))}
          onClose={handleClose}
        />
      ))}
    </>
  )
}

export default AgentMapLayers
